import {useReducer} from "react";
import type {GridPoint} from "@/dungeons/model/DungeonGeometry.ts";
import type {GeometryEditorTool, SelectedGeometryFeature} from "./GeometryEditorTypes.ts";

export type {GeometryEditorTool, SelectedGeometryFeature} from "./GeometryEditorTypes.ts";

export type DungeonEditorState = {
    tool: GeometryEditorTool;
    wallDraft: GridPoint[];
    transitionDraft: GridPoint[];
    doorDraft?: GridPoint;
    selection?: SelectedGeometryFeature;
};

export type DungeonEditorAction =
    | { type: "SET_TOOL"; tool: GeometryEditorTool }
    | { type: "ADD_WALL_POINT"; point: GridPoint }
    | { type: "WALL_CONTEXT_MENU" }
    | { type: "CLEAR_WALL_DRAFT" }
    | { type: "FINISH_WALL"; wallId: string }
    | { type: "ADD_TRANSITION_POINT"; point: GridPoint }
    | { type: "TRANSITION_CONTEXT_MENU" }
    | { type: "CLEAR_TRANSITION_DRAFT" }
    | { type: "FINISH_TRANSITION"; transitionId: string }
    | { type: "START_DOOR"; point: GridPoint }
    | { type: "FINISH_DOOR"; doorId: string }
    | { type: "DOOR_CONTEXT_MENU" }
    | { type: "CLEAR_DOOR_DRAFT" }
    | { type: "SELECT_FEATURE"; selection?: SelectedGeometryFeature };

export const createDungeonEditorState = (): DungeonEditorState => ({
    tool: "select",
    wallDraft: [],
    transitionDraft: [],
});

export const dungeonEditorReducer = (
    state: DungeonEditorState,
    action: DungeonEditorAction,
): DungeonEditorState => {
    switch (action.type) {
        case "SET_TOOL":
            return {...state, tool: action.tool, wallDraft: [], transitionDraft: [],
                doorDraft: undefined, selection: undefined};
        case "ADD_WALL_POINT": {
            const previous = state.wallDraft[state.wallDraft.length - 1];
            if (previous && previous.x === action.point.x && previous.y === action.point.y) return state;
            return {...state, wallDraft: [...state.wallDraft, action.point]};
        }
        case "WALL_CONTEXT_MENU":
            return state.wallDraft.length > 0
                ? {...state, wallDraft: state.wallDraft.slice(0, -1)}
                : {...state, tool: "select", selection: undefined};
        case "CLEAR_WALL_DRAFT":
            return {...state, wallDraft: []};
        case "FINISH_WALL":
            return state.wallDraft.length < 2 ? state : {
                ...state, wallDraft: [], selection: {type: "wall", id: action.wallId},
            };
        case "ADD_TRANSITION_POINT": {
            const previous = state.transitionDraft[state.transitionDraft.length - 1];
            if (previous && previous.x === action.point.x && previous.y === action.point.y) return state;
            return {...state, transitionDraft: [...state.transitionDraft, action.point]};
        }
        case "TRANSITION_CONTEXT_MENU":
            return state.transitionDraft.length > 0
                ? {...state, transitionDraft: state.transitionDraft.slice(0, -1)}
                : {...state, tool: "select", selection: undefined};
        case "CLEAR_TRANSITION_DRAFT":
            return {...state, transitionDraft: []};
        case "FINISH_TRANSITION":
            return {...state, transitionDraft: [], tool: "select",
                selection: {type: "transition", id: action.transitionId}};
        case "START_DOOR":
            return {...state, doorDraft: action.point};
        case "FINISH_DOOR":
            return {...state, doorDraft: undefined, tool: "select", selection: {type: "door", id: action.doorId}};
        case "DOOR_CONTEXT_MENU":
            return state.doorDraft
                ? {...state, doorDraft: undefined}
                : {...state, tool: "select", selection: undefined};
        case "CLEAR_DOOR_DRAFT":
            return {...state, doorDraft: undefined};
        case "SELECT_FEATURE":
            return {...state, selection: action.selection, wallDraft: [], transitionDraft: [], doorDraft: undefined};
    }
};

export const useDungeonEditorState = () => {
    const [state, dispatch] = useReducer(dungeonEditorReducer, undefined, createDungeonEditorState);
    return {state, dispatch};
};
